import { Form, Input } from "antd";
import React, { Dispatch, SetStateAction } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { Flex } from "src/components";
import { BasicButton } from "src/components/AuthenticationPage";
import Spacer from "src/components/base/Spacer";
import useSelector from "src/redux/store/useSelector";
import { dataType } from "src/types/authentication.type";
import { eRequirement } from "src/utils/regCheck";
import GoogleLoginButton, {
    StyledButton,
} from "src/components/AuthenticationPage/GoogleLoginButton";
import OrDivider from "src/components/AuthenticationPage/OrDivider";
import { authTitle, AUTH_TITLE } from "src/utils";
import TextButton from "src/components/AuthenticationPage/TextButton";
import { syncAutofillOnSubmit } from "src/components/AuthenticationPage/syncAutofill";

type LoginProps = {
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    handleLoginForm: (body: { email: string; password: string }) => void;
    setData: Dispatch<SetStateAction<dataType>>;
    data: dataType;
};

const Login: React.FC<LoginProps> = ({ setTitle, handleLoginForm, setData, data }) => {
    const [form] = Form.useForm();
    const loading = useSelector((state) => state.login?.loading || state.signup?.loading);

    const handleForget = async () => {
        const body = form.getFieldsValue();

        setData({
            ...data,
            email: body.email || "",
        });
        setTitle(intl.get("authentication.shared.forgotPassword"));
    };

    const handleRegister = () => {
        setTitle(authTitle(AUTH_TITLE.SIGNUP));
    };

    return (
        <Flex
            column
            align="center"
            justify="center"
            style={{ width: "100%" }}
            onSubmitCapture={syncAutofillOnSubmit(form)}
        >
            <Spacer height={16} />
            <Form
                layout={"vertical"}
                form={form}
                style={{ width: "100%" }}
                onFinish={(values) =>
                    handleLoginForm({ email: values.email, password: values.password })
                }
            >
                <Form.Item
                    name="email"
                    initialValue={data.email}
                    rules={[
                        {
                            required: true,
                            message: intl.get("authentication.warning.inputEmail"),
                        },
                        {
                            pattern: eRequirement,
                            message: intl.get("authentication.warning.inputValidEmail"),
                        },
                    ]}
                >
                    <Input
                        size="large"
                        autoComplete="username"
                        placeholder={intl.get("authentication.shared.email")}
                    />
                </Form.Item>
                <Form.Item
                    name="password"
                    style={{ marginBottom: 8 }}
                    rules={[
                        { required: true, message: intl.get("authentication.warning.inputPW") },
                    ]}
                >
                    <Input.Password
                        size="large"
                        autoComplete="current-password"
                        placeholder={intl.get("authentication.shared.pw")}
                    />
                </Form.Item>
                <ForgotRow>
                    <BasicButton
                        text={intl.get("authentication.shared.forgotPassword")}
                        handlePress={handleForget}
                        type="link"
                    />
                </ForgotRow>

                <Form.Item>
                    <StyledButton
                        loading={loading}
                        htmlType="submit"
                        block
                        type="primary"
                        size="large"
                        disabled={loading}
                    >
                        {intl.get("authentication.shared.continueWithEmail")}
                    </StyledButton>
                </Form.Item>
            </Form>
            <OrDivider />
            <GoogleLoginButton renderType="login" />
            <Spacer height={16} />
            <TextButton
                text={intl.get("authentication.shared.noAccount")}
                button={intl.get("authentication.shared.registerNow")}
                handlePress={handleRegister}
            />
        </Flex>
    );
};

const ForgotRow = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-bottom: 16px;

    .ant-btn-link {
        margin-left: -4px;
        font-size: 14px;
    }

    .ant-btn-link span {
        text-decoration: none;
    }
`;

export default Login;
